import React from "react"
import { Link } from "react-router-dom"
import {
  Avatar,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { Box } from "@mui/system"
import { useTheme } from "@mui/material/styles"
import { LICENSING_STEPS, LicensingStep } from "@coast/utils"
import { track } from "../../utils/analytics"
import StateSelect, { STATE_ABBVS } from "./StateSelect"

type Props = {
  usState?: string
  path?: string
  highlightId?: string
  controlsToShow?: Array<"path" | "state">
  articleLinkColor?: string
}

type ViewProps = {
  steps: LicensingStep[]
  highlightId?: string
  articleLink?: string
  articleLinkColor?: string
}

const handleActionButtonClicked = (buttonContext: string) => {
  track("Licensing Section Action Button Clicked", { buttonContext })
}

const HowToGetLicenseView = ({
  steps,
  highlightId,
  articleLink,
  articleLinkColor,
}: ViewProps) => {
  const theme = useTheme()
  return (
    <>
      <Grid
        container
        justifyContent="space-evenly"
        alignItems="stretch"
        spacing={2}
      >
        {steps.map((step, i) => (
          <Grid item xs={12} sm={6} md={3} lg={2} key={step.id}>
            <Card
              className={step.id === highlightId ? "top-accent" : undefined}
              data-cy="how-to-get-license-step"
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      height: 24,
                      width: 24,
                      backgroundColor: theme.palette.primary.main,
                      mr: 1,
                    }}
                  >
                    {i + 1}
                  </Avatar>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", lineHeight: 1.1 }}
                  >
                    {step.title}
                  </Typography>
                </Box>
                {step.id === highlightId && (
                  <Typography sx={{ mb: 1.5 }} color="text.secondary">
                    You are here!
                  </Typography>
                )}
                <Typography variant="body2">{step.description}</Typography>
              </CardContent>
              {step.link && step.id !== highlightId ? (
                <CardActions>
                  {step.link.startsWith("http") ||
                  step.link.startsWith("tel:") ? (
                    <Button
                      size="small"
                      href={step.link}
                      target="_blank"
                      rel="noreferrer"
                      variant="outlined"
                      onClick={() => handleActionButtonClicked(step.id)}
                    >
                      {step.linkText}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      component={Link}
                      to={step.link}
                      variant="outlined"
                      onClick={() => handleActionButtonClicked(step.id)}
                    >
                      {step.linkText}
                    </Button>
                  )}
                </CardActions>
              ) : null}
            </Card>
          </Grid>
        ))}
      </Grid>
      {articleLink && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          For more licensing details,{" "}
          <Link
            to={articleLink}
            style={{ color: articleLinkColor ?? "inherit" }}
          >
            <u>visit our help article</u>
          </Link>
          .
        </Box>
      )}
    </>
  )
}

function getPathOrDefault(usState?: string, path?: string) {
  if (path) return path

  if (usState && !!LICENSING_STEPS[usState]) {
    return Object.keys(LICENSING_STEPS[usState])[0]
  }

  return undefined
}

const getStateOrUndefined = (usState?: string) => {
  if (usState && !!LICENSING_STEPS[usState]) {
    return usState
  }

  return undefined
}

const HowToGetLicense = ({
  usState,
  path,
  highlightId,
  controlsToShow,
  articleLinkColor,
}: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const [chosenState, setChosenState] = React.useState<string | undefined>(
    getStateOrUndefined(usState),
  )
  const [chosenPath, setChosenPath] = React.useState<string | undefined>(
    getPathOrDefault(usState, path),
  )

  const handleSelectState = (state: string | undefined) => {
    if (!state) return
    track("State Selected in Licensing Section", { state })
    setChosenPath(Object.keys(LICENSING_STEPS[state.toLowerCase()])[0])
    setChosenState(state.toLowerCase())
  }

  const controlComponents = controlsToShow
    ? controlsToShow.map((control) => {
        switch (control) {
          case "state":
            if (isMobile) {
              return (
                <Box sx={{ mb: 2 }}>
                  <StateSelect
                    label="Select Your State"
                    availableStates={STATE_ABBVS.filter(
                      (stateShort) =>
                        LICENSING_STEPS[stateShort.toLocaleLowerCase()],
                    )}
                    selectedState={chosenState?.toLocaleUpperCase()}
                    onSelectedCallback={(state) => handleSelectState(state)}
                  />
                </Box>
              )
            }
            return (
              <ButtonGroup
                sx={{ mr: 2, mb: 1, backgroundColor: "#fff" }}
                key="state"
                data-cy="state-button-group"
              >
                {Object.keys(LICENSING_STEPS).map((state) => (
                  <Button
                    key={state}
                    variant={chosenState === state ? "contained" : "outlined"}
                    onClick={() => handleSelectState(state)}
                  >
                    {state.toUpperCase()}
                  </Button>
                ))}
              </ButtonGroup>
            )
          case "path":
            return chosenState ? (
              <ButtonGroup
                sx={{ background: "#fff" }}
                key="path"
                data-cy="path-button-group"
              >
                {Object.keys(LICENSING_STEPS[chosenState]).map((pathId) => (
                  <Button
                    key={pathId}
                    variant={chosenPath === pathId ? "contained" : "outlined"}
                    onClick={() => {
                      track("Path Selected in Licensing Section", { pathId })
                      setChosenPath(pathId)
                    }}
                  >
                    {LICENSING_STEPS[chosenState][pathId].label}
                  </Button>
                ))}
              </ButtonGroup>
            ) : null
          default:
            return null
        }
      })
    : []

  return (
    <>
      {controlComponents.length > 0 ? (
        <Box sx={{ mb: 2, textAlign: "center" }}>
          {!chosenState && (
            <Box sx={{ mb: 2 }}>Choose your state to get started:</Box>
          )}
          {controlComponents}
        </Box>
      ) : null}
      {chosenState && chosenPath ? (
        <HowToGetLicenseView
          steps={LICENSING_STEPS[chosenState][chosenPath].steps}
          highlightId={highlightId}
          articleLink={LICENSING_STEPS[chosenState][chosenPath].article}
          articleLinkColor={articleLinkColor}
        />
      ) : null}
    </>
  )
}

export default HowToGetLicense
